import { Vector3 } from 'three';
import { DEFAULT_ONE_EURO, smoothingAlpha, type OneEuroParams } from './OneEuroFilter';

/**
 * One Euro filter for a 3D vector.
 *
 * Unlike three independent scalar filters, the cutoff is driven by the norm of
 * the (filtered) velocity vector, so all axes share the same responsiveness.
 * This avoids the "axis stair-stepping" artefact where a diagonal movement is
 * smoothed differently on X and Y.
 */
export class OneEuroVector3 {
  private params: OneEuroParams;
  private readonly hatX = new Vector3();
  private readonly hatDx = new Vector3();
  private readonly lastRaw = new Vector3();
  private hasValue = false;
  private lastTime: number | null = null;
  private lastCutoff = 0;
  private readonly tmp = new Vector3();

  constructor(params: Partial<OneEuroParams> = {}) {
    this.params = { ...DEFAULT_ONE_EURO, ...params };
  }

  setParams(params: Partial<OneEuroParams>): void {
    this.params = { ...this.params, ...params };
  }

  getParams(): OneEuroParams {
    return { ...this.params };
  }

  /** Filters `raw` in place into `out` (or a new vector) and returns it. */
  filter(raw: Vector3, tSeconds: number, out: Vector3 = new Vector3()): Vector3 {
    let dt = this.lastTime === null ? 1 / 60 : tSeconds - this.lastTime;
    if (!(dt > 0)) dt = 1 / 60;
    this.lastTime = tSeconds;

    if (!this.hasValue) {
      this.hatX.copy(raw);
      this.hatDx.set(0, 0, 0);
      this.lastRaw.copy(raw);
      this.hasValue = true;
      return out.copy(raw);
    }

    // Velocity estimate, low-passed at dCutoff.
    this.tmp.copy(raw).sub(this.lastRaw).divideScalar(dt);
    this.lastRaw.copy(raw);
    const aD = smoothingAlpha(this.params.dCutoff, dt);
    this.hatDx.lerp(this.tmp, aD);

    const speed = this.hatDx.length();
    const cutoff = this.params.minCutoff + this.params.beta * speed;
    this.lastCutoff = cutoff;
    const a = smoothingAlpha(cutoff, dt);
    this.hatX.lerp(raw, a);
    return out.copy(this.hatX);
  }

  /** Filtered velocity (units/second). */
  get velocity(): Vector3 {
    return this.hatDx;
  }

  /** Cutoff used by the last sample (Hz): the filter's lag at constant velocity is 1 / (2π · cutoff). */
  get currentCutoff(): number {
    return this.lastCutoff;
  }

  get value(): Vector3 | null {
    return this.hasValue ? this.hatX : null;
  }

  reset(): void {
    this.hasValue = false;
    this.lastTime = null;
    this.lastCutoff = 0;
    this.hatDx.set(0, 0, 0);
  }
}
